import { Route, Navigate } from "react-router-dom"
import { lazy } from "react"
import { AuthGuard, BusinessOperationalGuard } from "@/router/guards"
import { LazyPage } from "@/router/lazy-page"

// Layouts
import BusinessLayout from "@/layouts/business-layout"

// Business Pages (Moved to @/pages/business/*)
const Home = lazy(() => import("@/pages/business/home"))
const SelectBusiness = lazy(() => import("@/pages/auth/select-business"))
const SelectConsoleScope = lazy(() => import("@/pages/auth/select-console-scope"))
const BusinessesIndex = lazy(() => import("@/pages/business/businesses/index"))
const CreateBusiness = lazy(() => import("@/pages/business/businesses/create"))
const EditBusiness = lazy(() => import("@/pages/business/businesses/edit"))
const ShowBusiness = lazy(() => import("@/pages/business/businesses/show"))
const BusinessImport = lazy(() => import("@/pages/business/businesses/import"))

const ServicesPage = lazy(() => import("@/pages/business/services"))
const ServiceShow = lazy(() => import("@/pages/business/services/show"))
const ServiceEdit = lazy(() => import("@/pages/business/services/edit"))
const CreateServiceNew = lazy(() => import("@/pages/business/services/create"))

const BusinessProfile = lazy(() => import("@/pages/business/profiles/business"))
const UserProfile = lazy(() => import("@/pages/business/profiles/user"))
const ChangePassword = lazy(() => import("@/pages/business/profiles/change-password"))

const Appointments = lazy(() => import("@/pages/business/appointments"))
const CreateAppointment = lazy(() => import("@/pages/business/appointments/create"))
const AppointmentShow = lazy(() => import("@/pages/business/appointments/show"))
const SlotRequests = lazy(() => import("@/pages/business/slot-requests"))
const Customers = lazy(() => import("@/pages/business/customers"))
const CustomerShow = lazy(() => import("@/pages/business/customers/show"))
const Providers = lazy(() => import("@/pages/business/providers"))

const CreateProvider = lazy(() => import("@/pages/business/providers/create"))
const EditProvider = lazy(() => import("@/pages/business/providers/edit"))
const ShowProvider = lazy(() => import("@/pages/business/providers/show"))
const Finance = lazy(() => import("@/pages/business/finance"))
const BusinessSupport = lazy(() => import("@/pages/business/support"))
const BusinessChat = lazy(() => import("@/pages/business/chat"))

const AdCampaigns = lazy(() => import("@/pages/business/add-campaigns"))
const CreateCampaign = lazy(() => import("@/pages/business/add-campaigns/create"))
const CampaignShow = lazy(() => import("@/pages/business/add-campaigns/show"))
const CampaignEdit = lazy(() => import("@/pages/business/add-campaigns/edit"))

const SettingsLayout = lazy(() => import("@/pages/business/settings/settings-layout"))
const SettingsProfile = lazy(() => import("@/pages/business/settings/profile"))
const SettingsPassword = lazy(() => import("@/pages/business/settings/password"))
const SettingsAppearance = lazy(() => import("@/pages/business/settings/appearance"))
const SettingsLanguage = lazy(() => import("@/pages/business/settings/language"))
const SettingsNotifications = lazy(() => import("@/pages/business/settings/notifications"))
const SettingsBilling = lazy(() => import("@/pages/business/settings/billing"))

export const BusinessRoutes = () => (
    <Route element={<AuthGuard />}>
        <Route path="/select-business" element={<LazyPage fallback="none"><SelectBusiness /></LazyPage>} />
        <Route path="/select-console-scope" element={<LazyPage fallback="none"><SelectConsoleScope /></LazyPage>} />

        <Route element={<BusinessLayout />}>
            <Route path="/dashboard" element={<LazyPage fallback="dashboard"><Home /></LazyPage>} />
            <Route path="/business" element={<LazyPage fallback="list"><BusinessesIndex /></LazyPage>} />
            <Route path="/business/create" element={<LazyPage fallback="form"><CreateBusiness /></LazyPage>} />
            <Route path="/business/import" element={<LazyPage fallback="form"><BusinessImport /></LazyPage>} />
            <Route path="/business/:id" element={<LazyPage fallback="detail"><ShowBusiness /></LazyPage>} />
            <Route path="/business/:id/edit" element={<LazyPage fallback="form"><EditBusiness /></LazyPage>} />
            <Route path="/user-profile" element={<LazyPage fallback="form"><UserProfile /></LazyPage>} />
            <Route path="/change-password" element={<LazyPage fallback="form"><ChangePassword /></LazyPage>} />
            <Route element={<BusinessOperationalGuard />}>
            <Route path="/services" element={<LazyPage fallback="list"><ServicesPage /></LazyPage>} />
            <Route path="/services/create" element={<LazyPage fallback="form"><CreateServiceNew /></LazyPage>} />
            <Route path="/services/:id" element={<LazyPage fallback="detail"><ServiceShow /></LazyPage>} />
            <Route path="/services/:id/edit" element={<LazyPage fallback="form"><ServiceEdit /></LazyPage>} />
            <Route path="/appointments" element={<LazyPage fallback="list"><Appointments /></LazyPage>} />
            <Route path="/appointments/create" element={<LazyPage fallback="form"><CreateAppointment /></LazyPage>} />
            <Route path="/appointments/:id" element={<LazyPage fallback="detail"><AppointmentShow /></LazyPage>} />
            <Route path="/slot-requests" element={<LazyPage fallback="list"><SlotRequests /></LazyPage>} />
            <Route path="/customers" element={<LazyPage fallback="list"><Customers /></LazyPage>} />
            <Route path="/customers/:id" element={<LazyPage fallback="detail"><CustomerShow /></LazyPage>} />
            <Route path="/providers" element={<LazyPage fallback="list"><Providers /></LazyPage>} />
            <Route path="/providers/create" element={<LazyPage fallback="form"><CreateProvider /></LazyPage>} />
            <Route path="/providers/:id" element={<LazyPage fallback="detail"><ShowProvider /></LazyPage>} />
            <Route path="/providers/:id/edit" element={<LazyPage fallback="form"><EditProvider /></LazyPage>} />

            <Route path="/finance" element={<LazyPage fallback="list"><Finance /></LazyPage>} />
            <Route path="/income-management" element={<Navigate to="/finance?tab=overview" replace />} />
            <Route path="/transactions" element={<Navigate to="/finance?tab=transactions" replace />} />
            <Route path="/support" element={<LazyPage fallback="list"><BusinessSupport /></LazyPage>} />
            <Route path="/messages" element={<LazyPage fallback="list"><BusinessChat /></LazyPage>} />
            <Route path="/messages/:conversationId" element={<LazyPage fallback="list"><BusinessChat /></LazyPage>} />
            <Route path="/reports" element={<Navigate to="/support?tab=customer-issues" replace />} />
            <Route path="/ad-campaigns" element={<LazyPage fallback="list"><AdCampaigns /></LazyPage>} />
            <Route path="/ad-campaigns/add-camp" element={<Navigate to="/ad-campaigns/create" replace />} />
            <Route path="/ad-campaigns/create" element={<LazyPage fallback="form"><CreateCampaign /></LazyPage>} />
            <Route path="/ad-campaigns/:id" element={<LazyPage fallback="detail"><CampaignShow /></LazyPage>} />
            <Route path="/ad-campaigns/:id/edit" element={<LazyPage fallback="form"><CampaignEdit /></LazyPage>} />
            </Route>
            <Route path="/settings" element={<LazyPage fallback="list"><SettingsLayout /></LazyPage>}>
                <Route index element={<Navigate to="profile" replace />} />
                <Route path="profile" element={<LazyPage fallback="form"><SettingsProfile /></LazyPage>} />
                <Route path="business" element={<LazyPage fallback="form"><BusinessProfile /></LazyPage>} />
                <Route path="password" element={<LazyPage fallback="form"><SettingsPassword /></LazyPage>} />
                <Route path="appearance" element={<LazyPage fallback="form"><SettingsAppearance /></LazyPage>} />
                <Route path="language" element={<LazyPage fallback="form"><SettingsLanguage /></LazyPage>} />
                <Route path="notifications" element={<LazyPage fallback="list"><SettingsNotifications /></LazyPage>} />
                <Route path="billing" element={<LazyPage fallback="form"><SettingsBilling /></LazyPage>} />
            </Route>
            <Route path="/business-profile" element={<Navigate to="/settings/business" replace />} />
        </Route>
    </Route>
)
